<template>
	<div class="search-page pb-12">
		<v-container>
			<!-- Top Search Bar Card -->
			<v-card class="search-header-card pa-4 mb-6 rounded-xl" flat outlined>
				<v-row align="center">
					<v-col cols="12" :md="categories && categories.length > 1 ? 8 : 12">
						<v-text-field
							clearable
							outlined
							rounded
							dense
							hide-details
							@input="debounce"
							@keyup.enter="debounce"
							prepend-inner-icon="mdi-magnify"
							:placeholder="
								category &&
								api.objects['categorias-productos?scope[onlyStore]=1'] &&
								api.objects['categorias-productos?scope[onlyStore]=1'].collection[category]
									? `${$options.filters.trans('store.search_in')} ${
											api.objects['categorias-productos?scope[onlyStore]=1'].collection[category].nombre
									  }`
									: $options.filters.trans('store.search_in_all_store')
							"
							v-model="query"
							class="main-search-input"
						></v-text-field>
					</v-col>
					<v-col cols="12" md="4" v-if="categories && categories.length > 1">
						<v-select
							:items="categories"
							item-text="nombre"
							item-value="id"
							v-model="category"
							clearable
							outlined
							rounded
							dense
							hide-details
							prepend-inner-icon="mdi-tag-outline"
							:placeholder="'category' | trans"
							@change="debounce"
							class="category-select"
						></v-select>
					</v-col>
				</v-row>
			</v-card>

			<v-row>
				<!-- Filter Sidebar -->
				<v-col cols="12" md="3" class="filter-sidebar">
					<v-card class="filter-card pa-4 mb-4 rounded-xl" flat outlined>
						<h3 class="text-subtitle-2 font-weight-bold text-slate-800 mb-3 text-capitalize">
							<v-icon small color="primary" class="mr-1">mdi-tune</v-icon>
							{{ "filter" | trans }}
						</h3>

						<!-- Sort Selector -->
						<div class="mb-4">
							<label class="caption font-weight-bold text-slate-700 d-block mb-1">{{ "store.sort_by" | trans }}</label>
							<v-select
								outlined
								rounded
								dense
								hide-details
								:items="options"
								item-text="title"
								item-value="value"
								v-model="order"
								@change="filter"
							></v-select>
						</div>

						<!-- Price Range -->
						<div class="mb-4">
							<label class="caption font-weight-bold text-slate-700 d-block mb-1">{{ "price" | trans }}</label>
							<v-row dense>
								<v-col cols="6">
									<v-text-field
										@keyup.enter="filter"
										type="number"
										min="0"
										v-model="minPrice"
										dense
										outlined
										rounded
										hide-details
										:label="'store.from' | trans"
									></v-text-field>
								</v-col>
								<v-col cols="6">
									<v-text-field
										@keyup.enter="filter"
										type="number"
										:min="minPrice || 0"
										v-model="maxPrice"
										dense
										outlined
										rounded
										hide-details
										:label="'store.to_price' | trans"
									></v-text-field>
								</v-col>
							</v-row>
							<v-btn
								small
								depressed
								color="#0f172a"
								dark
								block
								class="mt-2 rounded-pill text-capitalize"
								@click="filter"
							>
								<span>{{ "crud.apply" | trans }}</span>
							</v-btn>
						</div>

						<!-- Clear Filters Button -->
						<v-btn
							text
							small
							block
							color="error"
							class="rounded-pill text-capitalize"
							v-if="query || category || minPrice || maxPrice"
							@click="
								query = '';
								minPrice = null;
								maxPrice = null;
								category = null;
								filter();
							"
						>
							<v-icon x-small left>mdi-filter-remove-outline</v-icon>
							{{ "store.clear_filters" | trans }}
						</v-btn>
					</v-card>

					<!-- Featured Products Sidebar Section -->
					<div v-if="starred.length" class="hidden-sm-and-down">
						<h3 class="text-subtitle-2 font-weight-bold text-slate-800 mb-3">
							<v-icon small color="amber darken-1" class="mr-1">mdi-star</v-icon>
							{{ "starreds" | trans }}
						</h3>
						<div v-for="p in starred" :key="p.id" class="mb-3">
							<product-card :product="p" @click:image="openDialog(p)"></product-card>
						</div>
					</div>
				</v-col>

				<!-- Results Catalog Area -->
				<v-col cols="12" md="9">
					<!-- Catalog Toolbar -->
					<div class="catalog-toolbar pa-3 mb-4 rounded-xl d-flex align-center justify-space-between flex-wrap">
						<div class="caption text-secondary-custom">
							<span v-if="loading">Buscando productos...</span>
							<span v-else>
								<strong>{{ products.length }}</strong> {{ "products" | trans }}
								{{ "store.with_associated_filters" | trans }}
							</span>
						</div>

						<v-btn-toggle v-model="mode" mandatory borderless dense class="rounded-pill mode-toggle">
							<v-btn small value="0" class="px-3">
								<v-icon small>mdi-view-grid</v-icon>
							</v-btn>
							<v-btn small value="1" class="px-3">
								<v-icon small>mdi-view-list</v-icon>
							</v-btn>
						</v-btn-toggle>
					</div>

					<v-progress-linear indeterminate v-if="loading" color="primary" class="mb-4 rounded"></v-progress-linear>

					<!-- Grid View -->
					<v-row v-if="mode == 0 && products.length > 0">
						<v-col cols="12" sm="6" lg="4" v-for="p in products" :key="p.id" class="d-flex">
							<product-card :product="p" @click:image="openDialog(p)" class="flex-grow-1"></product-card>
						</v-col>
					</v-row>

					<!-- List View -->
					<div v-else-if="mode == 1 && products.length > 0">
						<template v-for="p in products">
							<product-list :product="p" @click:image="openDialog(p)" :key="p.id"></product-list>
						</template>
					</div>

					<!-- Empty State -->
					<v-card
						class="empty-search-card pa-12 text-center rounded-xl"
						flat
						outlined
						v-else-if="products.length === 0 && !loading"
					>
						<v-avatar size="64" color="#f1f5f9" class="mb-4">
							<v-icon size="32" color="#94a3b8">mdi-magnify-remove-outline</v-icon>
						</v-avatar>
						<h3 class="text-subtitle-1 font-weight-bold text-slate-800 mb-1">
							{{ "store.not_found" | trans }} {{ "products" | trans }}
						</h3>
						<p class="caption text-secondary-custom mb-4">
							No encontramos resultados para tu búsqueda. Intenta con otros términos o limpia los filtros.
						</p>
						<div class="d-flex justify-center flex-wrap">
							<v-btn
								depressed
								rounded
								small
								color="primary"
								class="ma-1 text-capitalize"
								@click="
									query = '';
									minPrice = null;
									maxPrice = null;
									category = null;
									filter();
								"
							>
								<v-icon left x-small>mdi-filter-remove</v-icon>
								{{ "store.clear_filters" | trans }}
							</v-btn>
							<v-btn outlined rounded small color="primary" class="ma-1 text-capitalize" to="/">
								<v-icon left x-small>mdi-home</v-icon>
								{{ "store.back_to_home" | trans }}
							</v-btn>
						</div>
					</v-card>
				</v-col>
			</v-row>
		</v-container>

		<!-- Quick View Modal Dialog -->
		<v-dialog v-model="dialog" max-width="1000px" scrollable content-class="quick-view-dialog">
			<v-card v-if="product" class="modern-dialog-card rounded-xl">
				<v-btn
					icon
					class="dialog-close-btn"
					@click="
						dialog = false;
						product = null;
					"
				>
					<v-icon>mdi-close</v-icon>
				</v-btn>
				<product-description :product="product"></product-description>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import ProductCard from "@/components/ProductCard";
import ProductList from "@/components/ProductList";
import ProductDescription from "@/components/ProductDescription";

var timeout;
export default {
	name: "Search",
	components: { ProductCard, ProductDescription, ProductList },
	async mounted() {
		this.query = this.$route.query.query || "";
		this.filter();
		try {
			this.categories = await this.api.load(
				"categorias-productos?where[depth]=0&scope[withActiveProducts]=1&scope[onlyStore]=1"
			);
		} catch (e) {
			console.error(e);
		}
		this.starred = (await this.api.get(`productos?limit=3&inRandomOrder=1&where[destacado]=1&scope[onlyStore]=1`)).data;
	},
	data() {
		return {
			loading: false,
			category: null,
			query: "",
			mode: 0,
			dialog: false,
			product: null,
			products: [],
			starred: [],
			categories: [],
			order: "name",
			options: [
				{
					title: "Menor Precio",
					description: "",
					value: "precio",
				},
				{
					title: "Mayor Precio",
					description: "",
					value: "-precio",
				},
				{
					title: "Nombre (Desc)",
					description: "",
					value: "name",
				},
				{
					title: "Nombre (Asc)",
					description: "",
					value: "-name",
				},
			],
			minPrice: null,
			maxPrice: null,
		};
	},
	methods: {
		openDialog(product) {
			this.dialog = true;
			this.product = product;
		},
		debounce() {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				this.filter();
			}, 750);
		},
		async filter() {
			this.loading = true;
			if (this.$route.query.query != this.query) {
				this.$router.replace({ path: this.$route.path, query: { query: this.query } }).catch(() => {});
			}
			try {
				var uri = `productos?q=${this.query || ""}&limit=50&order=${this.order}&scope[onlyStore]=1`;
				if (this.category) {
					uri += `&scope[OfCategoria]=${this.category}`;
				}
				if (this.minPrice > 0) {
					uri += `&whereGte[precio]=${this.minPrice}`;
				}
				if (this.maxPrice > 0) {
					uri += `&whereLte[precio]=${this.maxPrice}`;
				}
				this.products = (await this.api.get(uri)).data;
			} catch (error) {
				this.api.showError(error);
			}
			this.loading = false;
		},
	},
};
</script>

<style scoped>
.search-page {
	background-color: #f8fafc;
	min-height: 100vh;
}

.search-header-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
	box-shadow: 0 4px 16px -2px rgba(15, 23, 42, 0.04) !important;
}

.filter-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
}

.catalog-toolbar {
	background-color: #ffffff;
	border: 1px solid #eef2f6;
}

.mode-toggle {
	background-color: #f1f5f9;
	border-radius: 20px;
}

.empty-search-card {
	background-color: #ffffff !important;
	border: 1px dashed #cbd5e1 !important;
}

.text-slate-800 {
	color: #1e293b;
}

.text-slate-700 {
	color: #334155;
}

.text-secondary-custom {
	color: #64748b;
}

.modern-dialog-card {
	background-color: #ffffff !important;
	position: relative;
}

.dialog-close-btn {
	position: absolute;
	right: 12px;
	top: 12px;
	z-index: 10;
	background-color: rgba(241, 245, 249, 0.8) !important;
}
</style>
